import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Quote } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { aboutSections } from '../mock';

const About = () => {
  const { theme } = useTheme();
  return (
    <div style={{ background: theme.body, color: theme.bodyText }} className="transition-colors duration-500">
      {/* HEADER — Logo left, About content right */}
      <section className="py-20 lg:py-24 px-6 lg:px-10" style={{ background: theme.sectionAlt }}>
        <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-10 items-center">
          {/* Left: Logo */}
          <div className="md:col-span-5 flex justify-center md:justify-start">
            <div
              className="relative w-[280px] md:w-[340px] lg:w-[400px] aspect-square rounded-full overflow-hidden"
              style={{
                background: theme.nav,
                border: `3px solid ${theme.brand}`,
                boxShadow: `0 24px 60px -20px rgba(0,0,0,0.5), 0 0 0 8px ${theme.cardBorder}`,
              }}
            >
              <img
                src={theme.logo}
                alt="SVF crest"
                className="w-full h-full object-contain"
                style={{ padding: theme.logoPadding || '0%' }}
              />
            </div>
          </div>

          {/* Right: Title + intro */}
          <div className="md:col-span-7">
            <div className="text-xs tracking-[0.3em] mb-3" style={{ color: theme.brand }}>— WHO WE ARE —</div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-semibold mb-6 leading-tight" style={{ color: theme.brand }}>
              About the Foundation
            </h1>
            <p className="text-lg leading-relaxed mb-5" style={{ color: theme.bodyText }}>
              The Sagi Venkata Narasimham Foundation is a modern institution rooted in an ancient lineage — dedicated to the disciplined study, lived practice, and joyful sharing of Indic wisdom.
            </p>
            <p className="text-base leading-relaxed" style={{ color: theme.muted }}>
              Through our three wings — SVLC, SVIH, and SVVS — we preserve the languages, intellectual heritage, and Vedic sciences of Bharata, and offer them as living gifts to the world.
            </p>
            <Link
              to="/gallery"
              className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium tracking-wide transition-all duration-300 hover:gap-3 hover:scale-[1.03]"
              style={{ background: theme.accent, color: theme.accentText }}
            >
              See Our Work <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* OUR INSPIRATION */}
<section
  className="py-20 px-6 lg:px-10"
  style={{ background: theme.sectionAlt }}
>
  <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-16 items-center">

    {/* Portrait */}
    <div className="md:col-span-5 flex flex-col items-center md:-ml-20">
      <img
        src="/images/sri-sagi-venkata-narasimham..png"
        alt="Sri Sagi Venkata Narasimham"
        className="rounded-3xl shadow-2xl w-[390px] md:w-[450px] object-cover"
        style={{
          border: `3px solid ${theme.brand}`,
        }}
      />

      <p
        className="mt-5 w-[450px] text-center text-[15px] font-semibold uppercase tracking-[0.02em]"
        style={{ color: theme.brand }}
      >
        Scholar • Spiritual Seeker • Inspiration Behind SVF
      </p>
    </div>

    {/* Text */}
    <div className="md:col-span-7 md:-mt-14">

     <div className="max-w-[720px]">

      <div
        className="text-xs tracking-[0.3em] mb-3"
        style={{ color: theme.brand }}
      >
        — OUR INSPIRATION —
      </div>

      <h2
        className="font-serif text-3xl lg:text-[2.8rem] xl:text-[2.75rem] mb-6 leading-tight"
        style={{ color: theme.brand }}
      >
        Every institution has an inspiration.
      </h2>

      <p
        className="text-lg leading-relaxed mb-6"
        style={{ color: theme.bodyText }}
      >
        Ours began with one man's lifelong devotion to Sanskrit,
        Vedic learning, spiritual discipline, and quiet service.
      </p>

      <p
        className="text-base leading-relaxed mb-9"
        style={{ color: theme.muted }}
      >
        That quiet life of scholarship and selfless service became
        the inspiration behind the Sagi Venkata Narasimham Foundation —
        an enduring effort to preserve, nurture, and share the eternal
        wisdom of Bharata with future generations.
      </p>

      <Link
        to="#"
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all duration-300 hover:gap-3 hover:scale-[1.03]"
        style={{
          background: theme.accent,
          color: theme.accentText,
        }}
      >
        Discover His Legacy
        <ArrowRight size={18} />
      </Link>

    </div>

  </div>

   </div>
</section>

      {/* QUOTE */}
      <section className="py-16 px-6 lg:px-10" style={{ background: theme.body }}>
        <div className="max-w-4xl mx-auto text-center">
          <Quote size={36} style={{ color: theme.brand }} className="mx-auto mb-5 opacity-70" />
          <p className="font-serif text-2xl md:text-3xl italic leading-snug" style={{ color: theme.brand }}>
            “Sa Vidya Ya Vimuktaye — That alone is true knowledge which liberates.”
          </p>
          <div className="mt-4 text-sm tracking-widest" style={{ color: theme.muted }}>— Vishnu Purana</div>
        </div>
      </section>

      {/* SECTIONS */}
      <section className="py-12 px-6 lg:px-10" style={{ background: theme.body }}>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-7">
          {aboutSections.map((s) => (
            <div
              key={s.title}
              className="rounded-2xl p-8 transition-transform duration-500 hover:-translate-y-1"
              style={{
                background: theme.cardBg,
                border: `1px solid ${theme.cardBorder}`,
                boxShadow: '0 20px 50px -25px rgba(0,0,0,0.35)',
              }}
            >
              <h3 className="font-serif text-2xl mb-4" style={{ color: theme.brand }}>{s.title}</h3>
              <p className="leading-relaxed" style={{ color: theme.cardText }}>{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-10" style={{ background: theme.sectionAlt }}>
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-4xl mb-5" style={{ color: theme.brand }}>Walk With Us</h2>
          <p className="mb-8" style={{ color: theme.muted }}>
            Whether you are a scholar, a student, a seeker, or a supporter — there is a place for you in this work.
          </p>
          <Link
            to="/gallery"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-medium transition-all duration-300 hover:gap-3 hover:scale-[1.03]"
            style={{ background: theme.accent, color: theme.accentText }}
          >
            Explore Gallery <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
